import { OpenAPI } from './core/OpenAPI';
import { request as __request } from './core/request';
import type {
  CreateChildContactRelationshipRequest,
  UpdateChildContactRelationshipRequest,
  UpdatePermissionsRequest,
  ChildContactRelationshipResponse,
  ChildContactRelationshipWithDetailsResponse,
  ListRelationshipsResponse,
  AuditHistoryResponse
} from '@/types/childContactRelationship';

export class ChildContactRelationshipsService {
  /**
   * Create a new child-contact relationship
   * @param childId Child ID (UUID format)
   * @param requestBody Relationship details
   * @returns ChildContactRelationshipResponse Created relationship
   * @throws ApiError
   */
  public static async createRelationship(
    childId: string,
    requestBody: CreateChildContactRelationshipRequest
  ): Promise<ChildContactRelationshipResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/v1/children/{childId}/relationships',
      path: {
        'childId': childId,
      },
      body: requestBody,
      errors: {
        400: 'Invalid request data',
        401: 'Authentication required',
        403: 'Forbidden - Org ID missing',
        409: 'Relationship already exists',
        500: 'Internal server error',
      },
    });
  }

  /**
   * List relationships for a specific child
   * @param childId Child ID
   * @param limit Maximum number of results
   * @param offset Offset for pagination
   * @returns ListRelationshipsResponse List of relationships
   * @throws ApiError
   */
  public static async listRelationshipsByChild(
    childId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ListRelationshipsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/children/{childId}/relationships',
      path: {
        'childId': childId,
      },
      query: {
        'limit': limit,
        'offset': offset,
      },
      errors: {
        400: 'Invalid request data',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Get emergency contacts for a child
   * @param childId Child ID
   * @returns ListRelationshipsResponse List of emergency contacts
   * @throws ApiError
   */
  public static async getEmergencyContacts(
    childId: string
  ): Promise<ListRelationshipsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/children/{childId}/relationships/emergency',
      path: {
        'childId': childId,
      },
      errors: {
        400: 'Invalid request data',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Get pickup-authorized contacts for a child
   * @param childId Child ID
   * @returns ChildContactRelationshipWithDetailsResponse[] The pickup contacts (a bare array)
   * @throws ApiError
   */
  public static async getPickupContacts(
    childId: string
  ): Promise<ChildContactRelationshipWithDetailsResponse[]> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/children/{childId}/relationships/pickup',
      path: {
        'childId': childId,
      },
      errors: {
        400: 'Invalid request data',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * List relationships for a specific contact
   * @param contactId Contact ID
   * @param limit Maximum number of results
   * @param offset Offset for pagination
   * @returns ListRelationshipsResponse List of relationships
   * @throws ApiError
   */
  public static async listRelationshipsByContact(
    contactId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ListRelationshipsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/contacts/{contactId}/relationships',
      path: {
        'contactId': contactId,
      },
      query: {
        'limit': limit,
        'offset': offset,
      },
      errors: {
        400: 'Invalid request data',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Get a specific relationship
   * @param id Relationship ID
   * @returns ChildContactRelationshipWithDetailsResponse Relationship details
   * @throws ApiError
   */
  public static async getRelationship(
    id: string
  ): Promise<ChildContactRelationshipWithDetailsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/relationships/{id}',
      path: {
        'id': id,
      },
      errors: {
        404: 'Relationship not found',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Update a relationship
   * @param id Relationship ID
   * @param requestBody Update details
   * @returns ChildContactRelationshipResponse Updated relationship
   * @throws ApiError
   */
  public static async updateRelationship(
    id: string,
    requestBody: UpdateChildContactRelationshipRequest
  ): Promise<ChildContactRelationshipResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/v1/relationships/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      errors: {
        400: 'Invalid request data',
        404: 'Relationship not found',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Update relationship permissions
   * @param id Relationship ID
   * @param requestBody Permissions update
   * @returns ChildContactRelationshipResponse Updated relationship
   * @throws ApiError
   */
  public static async updatePermissions(
    id: string,
    requestBody: UpdatePermissionsRequest
  ): Promise<ChildContactRelationshipResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/v1/relationships/{id}/permissions',
      path: {
        'id': id,
      },
      body: requestBody,
      errors: {
        400: 'Invalid request data',
        404: 'Relationship not found',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Delete a relationship
   * @param id Relationship ID
   * @returns void
   * @throws ApiError
   */
  public static async deleteRelationship(
    id: string
  ): Promise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/v1/relationships/{id}',
      path: {
        'id': id,
      },
      errors: {
        404: 'Relationship not found',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Get audit history for a relationship
   * @param id Relationship ID
   * @param limit Maximum number of results
   * @param offset Offset for pagination
   * @returns AuditHistoryResponse Audit history
   * @throws ApiError
   */
  public static async getAuditHistory(
    id: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<AuditHistoryResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/relationships/{id}/audit',
      path: {
        'id': id,
      },
      query: {
        'limit': limit,
        'offset': offset,
      },
      errors: {
        404: 'Relationship not found',
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }

  /**
   * Get expired relationships
   * @param limit Maximum number of results
   * @param offset Offset for pagination
   * @returns ListRelationshipsResponse List of expired relationships
   * @throws ApiError
   */
  public static async getExpiredRelationships(
    limit: number = 50,
    offset: number = 0
  ): Promise<ListRelationshipsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/relationships/expired',
      query: {
        'limit': limit,
        'offset': offset,
      },
      errors: {
        401: 'Authentication required',
        500: 'Internal server error',
      },
    });
  }
}